<script setup lang="ts">
import BasisAreaTile, { type UploadSuggestion } from '@/components/dashboard/BasisAreaTile.vue'
import BasisUploadBox, { type AttachableType, type UploadOptions } from '@/components/dashboard/BasisUploadBox.vue'
import ReadinessAreaController from '@controllers/Investors/ReadinessAreaController'
import { router } from '@inertiajs/vue3'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BankReadinessData = App.Data.Dashboard.BankReadinessData
import ReadinessAreaData = App.Data.Dashboard.ReadinessAreaData

const props = defineProps<{
  readiness: BankReadinessData
  upload: UploadOptions
  uploadSuggestions: Record<string, UploadSuggestion>
}>()

const { t } = useI18n()

const target = ref<string | null>(null)
const documentType = ref<string | null>(null)

/** Vorschlag einer Kachel, dessen Träger noch nicht feststand – er greift, sobald er passt. */
const pendingSuggestion = ref<UploadSuggestion | null>(null)

const uploadAnchor = ref<HTMLElement | null>(null)

function hasType(attachableType: AttachableType, documentType: string | null): boolean {
  return props.upload.documentTypes[attachableType].some((option) => option.value === documentType)
}

/** Nur wenn es genau einen Träger gibt, ist die Vorauswahl eindeutig. */
function onlyTarget(suggestion: UploadSuggestion): string | null {
  if (suggestion.attachableType === 'natural-person') {
    return `natural-person:${props.upload.personId}`
  }

  const candidates = suggestion.attachableType === 'estate-object' ? props.upload.estateObjects : props.upload.companies

  return candidates.length === 1 ? `${suggestion.attachableType}:${candidates[0].id}` : null
}

function suggestUpload(suggestion: UploadSuggestion): void {
  const preselected = onlyTarget(suggestion)

  if (preselected) {
    target.value = preselected
    documentType.value = hasType(suggestion.attachableType, suggestion.documentType) ? suggestion.documentType : null
  } else {
    pendingSuggestion.value = suggestion
  }

  uploadAnchor.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

/** Die Dokumentarten hängen am Träger: was nicht mehr dazu passt, fällt weg. */
watch(target, (value) => {
  if (!value) {
    documentType.value = null

    return
  }

  const attachableType = value.split(':')[0] as AttachableType
  const suggestion = pendingSuggestion.value

  if (suggestion?.attachableType === attachableType && hasType(attachableType, suggestion.documentType)) {
    documentType.value = suggestion.documentType
    pendingSuggestion.value = null

    return
  }

  if (!hasType(attachableType, documentType.value)) {
    documentType.value = null
  }
})

function answer(area: ReadinessAreaData, applicable: boolean): void {
  router.patch(ReadinessAreaController(area.key).url, { applicable }, { preserveScroll: true })
}
</script>

<template>
  <section class="fh-card @container p-6">
    <p class="section-label section-label--sm">{{ t('eyebrow') }}</p>
    <h2 class="mt-1 text-xl font-bold tracking-tight">{{ t('title') }}</h2>
    <p class="text-ink-2 mt-3 max-w-[74ch]">{{ t('description') }}</p>

    <div class="mt-5 grid gap-4 @xl:grid-cols-2 @4xl:grid-cols-4">
      <BasisAreaTile
        v-for="area in readiness.areas"
        :key="area.key"
        :area="area"
        :suggestion="uploadSuggestions[area.key] ?? null"
        @answer="answer(area, $event)"
        @upload="suggestUpload" />
    </div>

    <div ref="uploadAnchor" class="mt-4">
      <BasisUploadBox v-model:target="target" v-model:document-type="documentType" :options="upload" />
    </div>
  </section>
</template>

<i18n>
{
  "en": {
    "eyebrow": "Your base",
    "title": "Enter it once, use it for every financing",
    "description": "You collect profile, credit rating, portfolio and companies here a single time. Every further request draws on it – the paperwork happens exactly once."
  },
  "de": {
    "eyebrow": "Deine Basis",
    "title": "Einmal eintragen, für jede Finanzierung nutzen",
    "description": "Profil, Bonität, Portfolio und Unternehmen sammelst du hier ein einziges Mal. Jede weitere Anfrage greift darauf zu – die Zettelwirtschaft machst du genau einmal."
  }
}
</i18n>
